/**
 * affiliateCookies.ts — Bundle 7.B Hotfix #2
 *
 * Resolves a raw ?aff=<code> URL param into the correct house + sub cookies.
 * Called by App.tsx on first page load (client-side, SPA / CloudFront edge).
 *
 * Why this exists: CloudFront caches index.html and serves it without hitting
 * Django middleware. App.tsx is the only thing running on a fresh SPA visit,
 * so it must set aff_house, aff_house_set_at, and aff_sub correctly.
 *
 * Semantics:
 *   - aff_house: first-touch-wins, 30 days. Always stores HOUSE code.
 *   - aff_house_set_at: timestamp of first aff_house write.
 *   - aff_sub: last-click-wins, 30 days. Stores sub code, or cleared on house visit.
 */

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MAX_AGE_30D = 30 * 24 * 60 * 60;

function writeCookie(name: string, value: string, maxAge: number): void {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${value}; max-age=${maxAge}; path=/; SameSite=Lax${secure}`;
}

function clearCookie(name: string): void {
  document.cookie = `${name}=; max-age=0; path=/; SameSite=Lax`;
}

function hasHouseCookie(): boolean {
  return /(?:^|;\s*)aff_house=/.test(document.cookie);
}

interface AffCodeInfo {
  code: string;
  is_house: boolean;
  house_code: string;
  is_active: boolean;
}

/**
 * resolveAffiliateCookies
 *
 * Given a raw ?aff= value (may be house or sub code):
 * 1. Calls /api/affiliate/code-info/<code>/ to resolve hierarchy
 * 2. Writes aff_house + aff_house_set_at (first-touch only)
 * 3. Writes aff_sub if sub code, clears it if house code
 */
export async function resolveAffiliateCookies(rawCode: string): Promise<void> {
  if (!rawCode) return;

  const cleaned = rawCode.trim().toLowerCase();
  if (cleaned.length > 64 || !SLUG_RE.test(cleaned)) return;

  try {
    const res = await fetch(`/api/affiliate/code-info/${cleaned}/`);
    if (!res.ok) {
      // Invalid or unknown code — don't write any cookies
      return;
    }
    const info: AffCodeInfo = await res.json();

    // First-touch protection: only write aff_house if not already set
    if (!hasHouseCookie()) {
      const houseCode = info.is_house ? info.code : info.house_code;
      writeCookie('aff_house', houseCode, MAX_AGE_30D);
      writeCookie('aff_house_set_at', String(Date.now()), MAX_AGE_30D);
    }

    // aff_sub: last-click-wins
    if (!info.is_house) {
      writeCookie('aff_sub', info.code, MAX_AGE_30D);
    } else {
      // House-direct visit: clear any stale sub cookie
      clearCookie('aff_sub');
    }
  } catch (err) {
    // Non-fatal: cookie resolution failing should never break the page
    console.warn('[affiliate] Cookie resolution failed:', err);
  }
}
